import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'

interface Order {
  id: string
  date: string
  customer: string
  status: 'pending' | 'in_transit' | 'completed'
  fare: number
  time: string
}

interface OrderCalendarProps {
  orders: Order[]
  onDateClick: (date: string) => void
  selectedDate: string | null
}

export function OrderCalendar({ orders, onDateClick, selectedDate }: OrderCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const getOrdersByDate = (dateStr: string) => {
    return orders.filter(order => order.date === dateStr)
  }

  const getOrderCountsByDate = (dateStr: string) => {
    const dayOrders = getOrdersByDate(dateStr)
    const total = dayOrders.length
    // Count pending orders - recognize various pending status values
    const pendingStatuses = ['pending', 'awaiting_assignment', 'awaiting_dispatch', 'unassigned']
    const pending = dayOrders.filter(o => {
      const statusLower = o.status.toLowerCase()
      const isPending = pendingStatuses.includes(statusLower) || 
                        o.status === '待派車' ||
                        o.status === '待派车'
      return isPending
    }).length
    return { total, pending }
  }

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
  }

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
  }

  const daysInMonth = getDaysInMonth(currentDate)
  const firstDay = getFirstDayOfMonth(currentDate)
  const days = []

  // 本月之前的空白格子
  for (let i = 0; i < firstDay; i++) {
    days.push(null)
  }

  // 本月的日期
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i)
  }

  const statusConfig = {
    pending: { color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400', label: '待派單' },
    in_transit: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', label: '運送中' },
    completed: { color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', label: '已完成' },
  }

  const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
  const weekDays = ['日', '一', '二', '三', '四', '五', '六']

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/50 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Weekdays Header */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-slate-600 dark:text-slate-400 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((day, idx) => {
          if (day === null) {
            return (
              <div key={`empty-${idx}`} className="aspect-square p-2 rounded-lg border border-transparent bg-slate-50 dark:bg-slate-800/30" />
            )
          }

          const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          const { total, pending } = getOrderCountsByDate(dateStr)
          const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString()
          const isSelected = selectedDate === dateStr

          return (
            <button
              key={day}
              onClick={() => onDateClick(dateStr)}
              className={`
                aspect-square p-2 rounded-lg border transition-all flex flex-col items-center justify-center relative
                ${isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : isToday
                  ? 'border-purple-300 dark:border-purple-700 bg-purple-100 dark:bg-purple-900/40'
                  : total > 0
                  ? 'border-slate-200 dark:border-slate-700 bg-green-50/50 dark:bg-green-900/20 hover:border-slate-300 dark:hover:border-slate-600'
                  : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:border-slate-300 dark:hover:border-slate-600'
                }
                ${isToday ? 'ring-2 ring-purple-400 dark:ring-purple-500 ring-inset' : ''}
              `}
            >
              {/* Badge Container - top right */}
              {(total > 0 || pending > 0) && (
                <div className="absolute flex gap-1 z-50" style={{ top: '-2px', right: '-2px' }}>
                  {/* Pending Count Badge - 黃土色 */}
                  {pending > 0 && (
                    <div className="bg-yellow-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg" title={`${pending}件待派單`}>
                      {pending > 99 ? '99+' : pending}
                    </div>
                  )}
                  {/* Total Count Badge - 紅色 */}
                  {total > 0 && (
                    <div className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-lg" title={`共${total}件訂單`}>
                      {total > 99 ? '99+' : total}
                    </div>
                  )}
                </div>
              )}

              <span className={`text-sm font-semibold ${isToday ? 'text-purple-600 dark:text-purple-400' : 'text-slate-900 dark:text-slate-50'}`}>
                {day}
              </span>

              {/* Small dot indicator */}
              {total > 0 && (
                <div className="absolute bottom-1 w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              )}
            </button>
          )
        })}
      </div>

      {/* Legend */}
      <div className="mt-6 space-y-2 text-xs text-slate-600 dark:text-slate-400">
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-purple-100 border border-purple-400 rounded" />
            <span>今日</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-50 border border-green-300 rounded" />
            <span>有訂單</span>
          </div>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-600 rounded-full" />
            <span>待派單數</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded-full" />
            <span>訂單總數</span>
          </div>
        </div>
      </div>
    </div>
  )
}
